// Client-side handle to the MuPDF worker (see worker.ts).
//
// Thin promise RPC: one in-flight map keyed by request id, ArrayBuffer
// results transferred (never copied). The worker is spawned lazily on
// first use so the ~5 MB wasm download only happens when the user
// actually opens the editor with a file.

import { makeEngineWorker } from "./worker-url.ts";
import type {
  Annotation,
  A11yFacts,
  A11yPatch,
  SignatureFieldSpec,
  DocFontInfo,
  DocInfo,
  DocInfoFields,
  AuthenticateResult,
  ContentDraw,
  ContentErase,
  FieldSpec,
  FillAction,
  FillInfo,
  FormValues,
  HeaderFooterConfig,
  OcrWord,
  OutlineItem,
  RegionItem,
  RegionMove,
  WatermarkConfig,
  ImageAction,
  OpenResult,
  PageInfo,
  PageOp,
  Quad,
  ScanHit,
  ScanPattern,
  SecurityOptions,
  TextHit,
  WidgetInfo,
  RenderResult,
  SelectionResult,
  WorkerOp,
  WorkerRequest,
  WorkerRequestMap,
  WorkerResponse,
  WorkerResponseMap,
} from "./protocol.ts";

interface Pending {
  resolve: (value: never) => void;
  reject: (err: Error) => void;
}

/** Where the engine worker and its wasm are served from.
 *
 *  The default — `new URL("./worker.ts", import.meta.url)` — is what a
 *  bundler needs to see in order to emit the worker as its own chunk,
 *  and it is how this package is consumed inside its own repo. It is
 *  also the thing that breaks the moment the JS is served from a
 *  different origin than the assets, because a Worker script URL must
 *  be same-origin.
 *
 *  So an embedder can point the engine somewhere explicit instead.
 *  MuPDF resolves `mupdf-wasm.wasm` relative to the worker's OWN url,
 *  so the wasm has to sit next to the worker file — which is how the
 *  published `dist/assets/` directory is laid out. */
let workerUrlOverride: string | null = null;

/** Serve the engine from `path` (a directory, absolute or
 *  origin-relative) rather than from wherever the bundler put it.
 *  Call before the first PdfEngine is constructed — the <PdfEditor>
 *  `assetPath` prop does exactly this. */
export function setEngineAssetPath(path: string): void {
  workerUrlOverride = `${path.replace(/\/$/, "")}/engine.worker.js`;
}

export class PdfEngine {
  private worker: Worker;
  private seq = 0;
  private pending = new Map<number, Pending>();
  private ready: Promise<void>;

  constructor() {
    this.worker = workerUrlOverride
      ? new Worker(workerUrlOverride, { type: "module" })
      : makeEngineWorker();
    let markReady: () => void;
    this.ready = new Promise((resolve) => {
      markReady = resolve;
    });
    this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const msg = event.data;
      if (msg.id === -1) {
        markReady();
        return;
      }
      const entry = this.pending.get(msg.id);
      if (!entry) return;
      this.pending.delete(msg.id);
      if (msg.ok) entry.resolve(msg.result as never);
      else entry.reject(new Error(msg.error));
    };
    this.worker.onerror = (event) => {
      const err = new Error(event.message || "PDF engine crashed");
      for (const entry of this.pending.values()) entry.reject(err);
      this.pending.clear();
    };
  }

  private async call<Op extends WorkerOp>(
    op: Op,
    args: WorkerRequestMap[Op],
    transfer: Transferable[] = []
  ): Promise<WorkerResponseMap[Op]> {
    await this.ready;
    this.seq += 1;
    const id = this.seq;
    const request: WorkerRequest<Op> = { id, op, args };
    return new Promise<WorkerResponseMap[Op]>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as never, reject });
      this.worker.postMessage(request, { transfer });
    });
  }

  /** Create a fresh single-page PDF; returns its bytes (not opened). */
  async blank(width: number, height: number): Promise<ArrayBuffer> {
    const { bytes } = await this.call("blank", { width, height });
    return bytes;
  }

  open(bytes: ArrayBuffer): Promise<OpenResult> {
    return this.call("open", { bytes }, [bytes]);
  }

  /** Unlock the currently open password-protected document. */
  authenticate(password: string): Promise<AuthenticateResult> {
    return this.call("authenticate", { password });
  }

  /** Find `needle` on one page; returns one Quad[] per hit. */
  async search(pageIndex: number, needle: string, maxHits = 256): Promise<Quad[][]> {
    const { hits } = await this.call("search", { pageIndex, needle, maxHits });
    return hits;
  }

  /** Structure boxes (blocks/lines/images) for the inspector overlay. */
  pageObjects(pageIndex: number): Promise<{
    blocks: [number, number, number, number][];
    lines: [number, number, number, number][];
    images: [number, number, number, number][];
    fills: { bbox: [number, number, number, number]; stroke: boolean; gradient: boolean }[];
  }> {
    return this.call("pageObjects", { pageIndex });
  }

  /** Apply a page operation; the result becomes the worker's open doc
   *  and is returned as the caller's new baseline bytes + page list. */
  pageOp(op: PageOp): Promise<{
    bytes: ArrayBuffer;
    pages: PageInfo[];
    widgets: WidgetInfo[];
    /** Crop moves the page origin — [pageIndex, dx, dy] view-space
     *  shifts to apply to that page's annotations. */
    offsets?: [number, number, number][];
  }> {
    return this.call("pageOp", { op });
  }

  /** Pull the document's own supported annotations into the overlay
   *  model, stripping them from the doc (bytes = rebase, or null if
   *  nothing was imported). */
  importAnnots(): Promise<{
    annotations: Annotation[];
    bytes: ArrayBuffer | null;
    pages: PageInfo[];
    widgets: WidgetInfo[];
  }> {
    return this.call("importAnnots", {});
  }

  /** Find the text line / paragraph (or image) under a page-space point. */
  textHit(
    pageIndex: number,
    x: number,
    y: number,
    mode: "line" | "block" = "line"
  ): Promise<TextHit> {
    return this.call("textHit", { pageIndex, x, y, mode });
  }

  /** Erase glyphs/an image and/or draw replacement text, rebasing the
   *  baseline like a page op. */
  editContent(
    pageIndex: number,
    erase: ContentErase | null,
    draw: ContentDraw | null
  ): Promise<{ bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] }> {
    return this.call("editContent", { pageIndex, erase, draw });
  }

  /** Find & replace across the document (or one hit via `only`). */
  replaceText(
    needle: string,
    replacement: string,
    only?: { pageIndex: number; quad: Quad }
  ): Promise<{
    count: number;
    bytes: ArrayBuffer | null;
    pages: PageInfo[];
    widgets: WidgetInfo[];
  }> {
    return this.call("replaceText", { needle, replacement, only });
  }

  /** Delete / move / replace a content image, rebasing the baseline. */
  imageOp(
    pageIndex: number,
    bbox: [number, number, number, number],
    action: ImageAction
  ): Promise<{ bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] }> {
    return this.call("imageOp", { pageIndex, bbox, action });
  }

  /** Burn headers/footers/page numbers and/or a watermark into the
   *  page content, rebasing the baseline. */
  decorate(cfg: {
    headerFooter?: HeaderFooterConfig;
    watermark?: WatermarkConfig;
  }): Promise<{ bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] }> {
    return this.call("decorate", cfg);
  }

  /** Open a second document for compare mode (independent of edits;
   *  sends a copy so the caller keeps its buffer). */
  compareOpen(bytes: ArrayBuffer): Promise<{ pageCount: number; pages: PageInfo[] }> {
    const copy = bytes.slice(0);
    return this.call("compareOpen", { bytes: copy }, [copy]);
  }

  compareRender(pageIndex: number, scale: number): Promise<RenderResult> {
    return this.call("compareRender", { pageIndex, scale });
  }

  compareClose(): Promise<Record<string, never>> {
    return this.call("compareClose", {});
  }

  /** Image export: hold a flattened copy, rasterise it, release it. */
  exportOpen(bytes: ArrayBuffer): Promise<{ pageCount: number; pages: PageInfo[] }> {
    return this.call("exportOpen", { bytes }, [bytes]);
  }

  exportRender(pageIndex: number, scale: number): Promise<RenderResult> {
    return this.call("exportRender", { pageIndex, scale });
  }

  exportClose(): Promise<Record<string, never>> {
    return this.call("exportClose", {});
  }

  /** Reduce file size. Transfers `baseline` — the caller must not reuse
   *  that buffer afterwards. */
  optimize(
    baseline: ArrayBuffer,
    images: { maxDpi: number; quality: number } | null
  ): Promise<{
    bytes: ArrayBuffer;
    pages: PageInfo[];
    widgets: WidgetInfo[];
    before: number;
    after: number;
    imagesRecompressed: number;
    imagesSeen: number;
    imageBytesSaved: number;
  }> {
    return this.call("optimize", { baseline, images }, [baseline]);
  }

  /** Every font the document references (usage + embedding). */
  async documentFonts(): Promise<DocFontInfo[]> {
    const { fonts } = await this.call("documentFonts", {});
    return fonts;
  }

  /** Whole-document pattern scan for find & redact (read-only). */
  scanText(
    patterns: ScanPattern[],
    maxHits = 2000
  ): Promise<{ hits: ScanHit[]; truncated: boolean; pagesScanned: number }> {
    return this.call("scanText", { patterns, maxHits });
  }

  /** Info dictionary, format, encryption state and permissions. */
  async documentInfo(): Promise<DocInfo> {
    const { info } = await this.call("documentInfo", {});
    return info;
  }

  /** Write document properties; rebases like any other document edit. */
  setDocumentInfo(
    fields: Partial<DocInfoFields>
  ): Promise<{ bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] }> {
    return this.call("setDocumentInfo", { fields });
  }

  /** Build the document to be signed: the finished export plus a
   *  signature dictionary with a hole where the signature goes. The
   *  crypto happens in the UI (see lib/sign-cms.ts) — this
   *  worker never sees the private key. */
  prepareSign(
    save: WorkerRequestMap["save"],
    field: SignatureFieldSpec
  ): Promise<WorkerResponseMap["prepareSign"]> {
    return this.call("prepareSign", { save, field });
  }

  /** Move or resize an existing form field's widget; rebases. */
  moveField(
    pageIndex: number,
    widgetIndex: number,
    rect: [number, number, number, number]
  ): Promise<{ bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] }> {
    return this.call("moveField", { pageIndex, widgetIndex, rect });
  }

  /** Register a session face for text-box embedding (see the worker's
   *  sessionFonts). Safe to call repeatedly with the same id. */
  registerFont(id: string, name: string, bytes: ArrayBuffer): Promise<void> {
    return this.call("registerFont", { id, name, bytes }, [bytes]).then(() => undefined);
  }

  /** What the document offers assistive technology. Facts only — the
   *  audit that reads them is lib/a11y.ts. */
  async accessibilityFacts(): Promise<A11yFacts> {
    const { facts } = await this.call("accessibilityFacts", {});
    return facts;
  }

  /** Apply the accessibility repairs that are a flag in the file;
   *  rebases like any other document edit. */
  setAccessibility(
    patch: A11yPatch
  ): Promise<{ bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] }> {
    return this.call("setAccessibility", { patch });
  }

  /** Objects (text/images/fills) inside a marquee rect. */
  async regionSelect(
    pageIndex: number,
    rect: [number, number, number, number]
  ): Promise<RegionItem[]> {
    const { items } = await this.call("regionSelect", { pageIndex, rect });
    return items;
  }

  /** The object under a point (modifier+click multi-select), optionally
   *  restricted to one kind. */
  async regionHit(
    pageIndex: number,
    x: number,
    y: number,
    kind?: "text" | "image" | "fill"
  ): Promise<RegionItem | null> {
    const { item } = await this.call("regionHit", { pageIndex, x, y, kind });
    return item;
  }

  /** Displace every listed object — one rebase. Returns the moved
   *  objects with FRESH ids: the rewrite renumbers them. */
  regionMove(
    pageIndex: number,
    moves: RegionMove[]
  ): Promise<{
    bytes: ArrayBuffer;
    pages: PageInfo[];
    widgets: WidgetInfo[];
    items: RegionItem[];
  }> {
    return this.call("regionMove", { pageIndex, moves });
  }

  /** Erase content objects / remove form fields — one rebase. */
  regionDelete(
    pageIndex: number,
    ids: string[]
  ): Promise<{ bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] }> {
    return this.call("regionDelete", { pageIndex, ids });
  }

  /** Add an interactive form field to a page, rebasing. Returns the
   *  name actually used (de-duplicated against existing fields). */
  createField(
    pageIndex: number,
    field: FieldSpec
  ): Promise<{
    bytes: ArrayBuffer;
    pages: PageInfo[];
    widgets: WidgetInfo[];
    name: string;
  }> {
    return this.call("createField", { pageIndex, field });
  }

  /** Page count + thumbnails for a PDF the user picked, without
   *  disturbing the open document. Sends a COPY: the caller keeps its
   *  buffer for the subsequent insert (which transfers it). */
  probePdf(
    bytes: ArrayBuffer,
    thumbWidth = 96,
    maxThumbs = 40
  ): Promise<{
    pageCount: number;
    thumbs: { pageIndex: number; width: number; height: number; pixels: ArrayBuffer }[];
  }> {
    const copy = bytes.slice(0);
    return this.call("probePdf", { bytes: copy, thumbWidth, maxThumbs }, [copy]);
  }

  /** Insert a page range from another PDF at `at`, rebasing. */
  insertPages(
    at: number,
    bytes: ArrayBuffer,
    from: number,
    to: number
  ): Promise<{ bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[]; count: number }> {
    return this.call("insertPages", { at, bytes, from, to }, [bytes]);
  }

  /** Build a NEW standalone PDF from the given pages, with the overlay
   *  annotations applied live and form values filled. Never touches
   *  the open document. */
  extractPages(
    indices: number[],
    annotations: Annotation[],
    formValues: FormValues
  ): Promise<{ bytes: ArrayBuffer }> {
    return this.call("extractPages", { indices, annotations, formValues });
  }

  /** Delete several pages in ONE rebase (thumbnail batch delete). */
  deletePages(
    indices: number[]
  ): Promise<{ bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] }> {
    return this.call("deletePages", { indices });
  }

  /** Draw recognised words as an invisible text layer (one rebase). */
  textLayer(
    pages: { pageIndex: number; words: OcrWord[] }[]
  ): Promise<{ bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] }> {
    return this.call("textLayer", { pages });
  }

  /** The document outline (bookmarks), flattened with a level per row. */
  async outline(): Promise<OutlineItem[]> {
    const { items } = await this.call("outline", {});
    return items;
  }

  /** Replace the whole outline, rebasing the baseline. */
  setOutline(
    items: OutlineItem[]
  ): Promise<{ bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] }> {
    return this.call("setOutline", { items });
  }

  /** Delete / move / recolour a vector fill block (or add a new one),
   *  rebasing the baseline. `fill` re-locates the block afterwards. */
  fillOp(
    pageIndex: number,
    id: string,
    action: FillAction
  ): Promise<{
    bytes: ArrayBuffer;
    pages: PageInfo[];
    widgets: WidgetInfo[];
    fill: FillInfo | null;
  }> {
    return this.call("fillOp", { pageIndex, id, action });
  }

  render(pageIndex: number, scale: number): Promise<RenderResult> {
    return this.call("render", { pageIndex, scale });
  }

  selection(
    pageIndex: number,
    p: [number, number],
    q: [number, number]
  ): Promise<SelectionResult> {
    return this.call("selection", { pageIndex, p, q });
  }

  /** Build (or rebuild) the markdown-import scratch document from a
   *  marked token tree + theme + resolved fonts/images. Independent of
   *  the open document (compare-mode pattern). Font/image bytes are
   *  copied, not transferred — the UI keeps them for rebuilds. */
  importMd(args: {
    tokens: unknown[];
    theme: import("./md-theme.ts").MdThemeConfig;
    faces: { key: string; name: string; bytes?: ArrayBuffer }[];
    images: Record<string, { dataUrl: string; width: number; height: number }>;
  }): Promise<{ pages: PageInfo[]; missingImages: string[] }> {
    const faces = args.faces.map((f) => (f.bytes ? { ...f, bytes: f.bytes.slice(0) } : f));
    return this.call("importMd", { ...args, faces });
  }

  /** Build (or rebuild) the spreadsheet-import scratch document —
   *  importMd's lifecycle (render/commit/close are shared). */
  importSheet(args: {
    sheets: import("./sheet-layout.ts").SheetData[];
    theme: import("./sheet-theme.ts").SheetThemeConfig;
    faces: { key: string; name: string; bytes?: ArrayBuffer }[];
    meta: import("./sheet-layout.ts").SheetLayoutMeta;
  }): Promise<{ pages: PageInfo[] }> {
    const faces = args.faces.map((f) => (f.bytes ? { ...f, bytes: f.bytes.slice(0) } : f));
    return this.call("importSheet", { ...args, faces });
  }

  /** Build (or rebuild) the image-import scratch document —
   *  importMd's lifecycle (render/commit/close are shared). */
  importPhotos(args: {
    items: import("./photo-layout.ts").PhotoItem[];
    theme: import("./photo-theme.ts").PhotoThemeConfig;
    faces: { key: string; name: string; bytes?: ArrayBuffer }[];
    images: Record<string, { dataUrl: string; width: number; height: number }>;
    meta: import("./sheet-layout.ts").SheetLayoutMeta;
  }): Promise<{ pages: PageInfo[] }> {
    const faces = args.faces.map((f) => (f.bytes ? { ...f, bytes: f.bytes.slice(0) } : f));
    return this.call("importPhotos", { ...args, faces });
  }

  /** Render a page of the import scratch document. */
  importMdRender(pageIndex: number, scale: number): Promise<RenderResult> {
    return this.call("importMdRender", { pageIndex, scale });
  }

  /** Finalize the import: save the scratch document, discard it, and
   *  return the bytes the preview was rendered from. */
  async importMdCommit(): Promise<ArrayBuffer> {
    const { bytes } = await this.call("importMdCommit", {});
    return bytes;
  }

  /** Discard the import scratch document. */
  importMdClose(): Promise<Record<string, never>> {
    return this.call("importMdClose", {});
  }

  async save(
    annotations: Annotation[],
    formValues: FormValues = {},
    opts: {
      flatten?: "none" | "forms" | "all";
      security?: SecurityOptions;
      stripMetadata?: boolean;
    } = {}
  ): Promise<Uint8Array> {
    const { bytes } = await this.call("save", {
      annotations,
      formValues,
      flatten: opts.flatten ?? "none",
      security: opts.security,
      stripMetadata: opts.stripMetadata,
    });
    return new Uint8Array(bytes);
  }

  destroy(): void {
    this.worker.terminate();
    const err = new Error("engine destroyed");
    for (const entry of this.pending.values()) entry.reject(err);
    this.pending.clear();
  }
}
